import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  CustomerServiceOrganizationLearningDocument,
  CustomerServiceOrganizationLearningQuery,
  CustomerServiceOrganizationLearningQueryVariables,
  GrantOrganizationLearningCourseDocument,
  GrantOrganizationLearningCourseMutation,
  GrantOrganizationLearningCourseMutationVariables,
  GrantOrganizationLearningPathDocument,
  GrantOrganizationLearningPathMutation,
  GrantOrganizationLearningPathMutationVariables,
  RevokeOrganizationLearningCourseDocument,
  RevokeOrganizationLearningCourseMutation,
  RevokeOrganizationLearningCourseMutationVariables,
  RevokeOrganizationLearningPathDocument,
  RevokeOrganizationLearningPathMutation,
  RevokeOrganizationLearningPathMutationVariables,
} from "../generated/graphql";
import { client } from "../lib/apolloClient";

type LearningCourseMetadata =
  CustomerServiceOrganizationLearningQuery["customerServiceLearningCatalog"]["courses"][number];

type LearningPathMetadata =
  CustomerServiceOrganizationLearningQuery["customerServiceLearningCatalog"]["learningPaths"][number];

export type OrganizationLearningCourse = LearningCourseMetadata & {
  granted: boolean;
};

export type OrganizationLearningPath = LearningPathMetadata & {
  granted: boolean;
  estimatedDurationMinutes: number;
  tags: string[];
};

type CatalogState = {
  courses: OrganizationLearningCourse[];
  learningPaths: OrganizationLearningPath[];
  loading: boolean;
  error: Error | null;
};

export class LearningGrantConflictError extends Error {
  constructor() {
    super("A grant update for this item is already running");
    this.name = "LearningGrantConflictError";
  }
}

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error("Learning catalog request failed");

const mapCatalog = (data: CustomerServiceOrganizationLearningQuery): Omit<CatalogState, "loading" | "error"> => {
  const activeCourseIds = new Set(
    data.organizationLearningGrants.grants
      .filter(grant => grant.active && grant.itemKind === "COURSE")
      .map(grant => grant.courseId)
      .filter((courseId): courseId is string => courseId != null)
  );
  const activePathIds = new Set(
    data.organizationLearningGrants.grants
      .filter(grant => grant.active && grant.itemKind === "LEARNING_PATH")
      .map(grant => grant.learningPathId)
      .filter((learningPathId): learningPathId is string => learningPathId != null)
  );
  const courses = data.customerServiceLearningCatalog.courses.map(course => ({
    ...course,
    granted: activeCourseIds.has(course.id),
  }));
  const coursesById = new Map(courses.map(course => [course.id, course]));
  const learningPaths = data.customerServiceLearningCatalog.learningPaths.map(path => {
    const memberCourses = [...path.courses]
      .sort((left, right) => left.position - right.position)
      .map(member => coursesById.get(member.courseId))
      .filter((course): course is OrganizationLearningCourse => course != null);

    return {
      ...path,
      granted: activePathIds.has(path.id),
      estimatedDurationMinutes: memberCourses.reduce((total, course) => total + course.estimatedDurationMinutes, 0),
      tags: Array.from(new Set(memberCourses.flatMap(course => course.tags))),
    };
  });

  return { courses, learningPaths };
};

export const useOrganizationLearningCatalog = (organizationId: string | null) => {
  const [state, setState] = useState<CatalogState>({
    courses: [],
    learningPaths: [],
    loading: organizationId != null,
    error: null,
  });
  const requestIdRef = useRef(0);
  const organizationIdRef = useRef(organizationId);
  organizationIdRef.current = organizationId;
  const pendingKeysRef = useRef(new Set<string>());
  const [pendingKeys, setPendingKeys] = useState<string[]>([]);

  const load = useCallback(
    async (preserveData = false): Promise<void> => {
      const requestId = ++requestIdRef.current;
      if (!organizationId) {
        setState({ courses: [], learningPaths: [], loading: false, error: null });
        return;
      }

      setState(previous => ({
        courses: preserveData ? previous.courses : [],
        learningPaths: preserveData ? previous.learningPaths : [],
        loading: !preserveData,
        error: null,
      }));

      try {
        const result = await client.query<
          CustomerServiceOrganizationLearningQuery,
          CustomerServiceOrganizationLearningQueryVariables
        >({
          query: CustomerServiceOrganizationLearningDocument,
          variables: { organizationId },
          fetchPolicy: "network-only",
        });
        if (requestId !== requestIdRef.current) return;
        setState({ ...mapCatalog(result.data), loading: false, error: null });
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        setState(previous => ({
          courses: preserveData ? previous.courses : [],
          learningPaths: preserveData ? previous.learningPaths : [],
          loading: false,
          error: toError(error),
        }));
      }
    },
    [organizationId]
  );

  useEffect(() => {
    void load();
    return () => {
      requestIdRef.current += 1;
    };
  }, [load]);

  const changeGrant = useCallback(
    async (itemKind: "course" | "learningPath", itemId: string, granted: boolean): Promise<void> => {
      if (!organizationId) throw new Error("No organization selected");
      const pendingKey = `${organizationId}:${itemKind}:${itemId}`;
      if (pendingKeysRef.current.has(pendingKey)) throw new LearningGrantConflictError();

      pendingKeysRef.current.add(pendingKey);
      setPendingKeys(Array.from(pendingKeysRef.current));
      try {
        let returnedActive: boolean | undefined;
        if (itemKind === "course") {
          if (granted) {
            const result = await client.mutate<
              GrantOrganizationLearningCourseMutation,
              GrantOrganizationLearningCourseMutationVariables
            >({
              mutation: GrantOrganizationLearningCourseDocument,
              variables: { input: { organizationId, courseId: itemId } },
            });
            returnedActive = result.data?.grantLearningCourse.grant.active;
          } else {
            const result = await client.mutate<
              RevokeOrganizationLearningCourseMutation,
              RevokeOrganizationLearningCourseMutationVariables
            >({
              mutation: RevokeOrganizationLearningCourseDocument,
              variables: { input: { organizationId, courseId: itemId } },
            });
            returnedActive = result.data?.revokeLearningCourse.grant.active;
          }
        } else {
          if (granted) {
            const result = await client.mutate<
              GrantOrganizationLearningPathMutation,
              GrantOrganizationLearningPathMutationVariables
            >({
              mutation: GrantOrganizationLearningPathDocument,
              variables: { input: { organizationId, learningPathId: itemId } },
            });
            returnedActive = result.data?.grantLearningPath.grant.active;
          } else {
            const result = await client.mutate<
              RevokeOrganizationLearningPathMutation,
              RevokeOrganizationLearningPathMutationVariables
            >({
              mutation: RevokeOrganizationLearningPathDocument,
              variables: { input: { organizationId, learningPathId: itemId } },
            });
            returnedActive = result.data?.revokeLearningPath.grant.active;
          }
        }

        if (returnedActive !== granted) throw new Error("Learning grant mutation returned conflicting state");
        if (organizationIdRef.current !== organizationId) return;
        setState(previous => ({
          ...previous,
          courses:
            itemKind === "course"
              ? previous.courses.map(course => (course.id === itemId ? { ...course, granted } : course))
              : previous.courses,
          learningPaths:
            itemKind === "learningPath"
              ? previous.learningPaths.map(path => (path.id === itemId ? { ...path, granted } : path))
              : previous.learningPaths,
        }));
      } finally {
        pendingKeysRef.current.delete(pendingKey);
        setPendingKeys(Array.from(pendingKeysRef.current));
      }
    },
    [organizationId]
  );

  const setCourseGrant = useCallback(
    (courseId: string, granted: boolean) => changeGrant("course", courseId, granted),
    [changeGrant]
  );
  const setLearningPathGrant = useCallback(
    (learningPathId: string, granted: boolean) => changeGrant("learningPath", learningPathId, granted),
    [changeGrant]
  );
  const pendingItemIds = useMemo(
    () =>
      new Set(
        pendingKeys.filter(key => key.startsWith(`${organizationId}:`)).map(key => key.slice(key.lastIndexOf(":") + 1))
      ),
    [organizationId, pendingKeys]
  );

  return {
    ...state,
    isEmpty: !state.loading && !state.error && state.courses.length === 0 && state.learningPaths.length === 0,
    pendingItemIds,
    reload: load,
    setCourseGrant,
    setLearningPathGrant,
  };
};
